import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getAllPromoters, getSalesByMonth, getAllTargets,
  getAttendanceByMonth, getAllBonusPrograms, getEventBonuses, getHolidays, getOvertimeByMonth,
  setIncomeHistory, addAuditLog,
  getPayrollAdjustments, setPayrollAdjustment, deletePayrollAdjustment,
  getPayrollClosing, setPayrollClosing,
  getInsuranceAdjustments, setInsuranceAdjustment,
} from "../../firebase/services";
import { getMgmtFeePerPerson, isProbationMonth, probationEndDate, computeMonthlyPay, promoterForMonth } from "../../firebase/schema";
import { format } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";
import { usePayrollConfig } from "../../hooks/usePayrollConfig";
import SalaryBreakdown from "../../components/SalaryBreakdown";
import { exportSalaryMatrixXLSX } from "../../utils/exportSalaryExcel";

// Chuẩn hoá tên để ghép (bỏ dấu, gộp khoảng trắng, lowercase)
const normName = (s) => (s || "").toString()
  .normalize("NFD").replace(/[̀-ͯ]/g, "")
  .replace(/\s+/g, " ").trim().toLowerCase();
// Parse tiền "VND 6,000,000" → 6000000
const parseMoney = (v) => {
  if (typeof v === "number") return v;
  const digits = (v || "").toString().replace(/[^\d]/g, "");
  return digits ? Number(digits) : 0;
};

export default function AdminSalary() {
  const { t } = useLanguage();
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // { matched: [], unmatched: [] }
  const [importMsg, setImportMsg] = useState("");
  const [importing, setImporting] = useState(false);
  // Review công + chốt sổ + chi phí công ty
  const [adjustments, setAdjustments] = useState([]);
  const [adjForm, setAdjForm] = useState({ promoterId: "", extraDays: "", extraBonus: "", otNonTax: "", note: "" });
  const [closing, setClosing] = useState(null);
  const [insAdjs, setInsAdjs] = useState([]);          // điều chỉnh diện đóng BH theo người
  const [showInsDetail, setShowInsDetail] = useState(false);
  const [exportingXls, setExportingXls] = useState(false);

  // Cấu hình áp dụng cho tháng đang xem (trang ⚙️ Cấu hình)
  const { cfgFor, loaded: cfgLoaded } = usePayrollConfig();
  const cfg = useMemo(() => cfgFor(month), [cfgFor, month]);
  const costCfg = cfg.cost;
  const taxCfg = cfg.tax;

  const isClosed = !!closing?.closed;

  const handleSaveAdjustment = async () => {
    if (!adjForm.promoterId || (adjForm.extraDays === "" && adjForm.extraBonus === "" && adjForm.otNonTax === "")) return;
    const p = rows.find(r => r.uid === adjForm.promoterId) || {};
    await setPayrollAdjustment(adjForm.promoterId, month, {
      extraDays: Number(adjForm.extraDays) || 0,
      extraBonus: Number(adjForm.extraBonus) || 0,
      extraOtNonTax: Number(adjForm.otNonTax) || 0,
      note: adjForm.note, promoterName: p.name || "",
    });
    setAdjForm({ promoterId: "", extraDays: "", extraBonus: "", otNonTax: "", note: "" });
    calculate();
  };

  const handleDeleteAdjustment = async (a) => {
    await deletePayrollAdjustment(a.promoterId, month);
    calculate();
  };

  const handleToggleClosing = async () => {
    const next = !isClosed;
    if (!window.confirm(next
      ? t("pr.close_confirm") : t("pr.reopen_confirm"))) return;
    await setPayrollClosing(month, next, "Admin");
    getPayrollClosing(month).then(setClosing);
  };

  // Bật/tắt diện đóng BH hoặc ghi đè mức lương đóng của một người trong tháng đang xem.
  // Cập nhật state ngay để bảng đổi tức thì — không tải lại cả bảng lương (tốn hàng chục truy vấn).
  const handleInsAdj = async (row, change) => {
    const enabled = change.enabled !== undefined ? change.enabled : row.enabled;
    const baseOverride = change.baseOverride !== undefined
      ? change.baseOverride
      : (row.overridden ? row.base : null);

    const prev = insAdjs;
    setInsAdjs(list => [
      ...list.filter(a => a.promoterId !== row.uid),
      { id: `${row.uid}_${month}`, promoterId: row.uid, month, enabled, baseOverride },
    ]);

    try {
      await setInsuranceAdjustment(row.uid, month, { enabled, baseOverride, promoterName: row.name });
    } catch (err) {
      setInsAdjs(prev); // ghi thất bại → trả bảng về đúng dữ liệu đang lưu
      alert("Lỗi lưu điều chỉnh bảo hiểm: " + err.message);
    }
  };

  const handleExportSalaryXls = async () => {
    setExportingXls(true);
    try {
      await exportSalaryMatrixXLSX({ rows, month, costCfg, taxCfg, workCfg: cfg.work, insAdjs });
    } catch (err) {
      alert("Lỗi xuất Excel: " + err.message);
    }
    setExportingXls(false);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportMsg("Đang đọc file...");
    setImportPreview(null);
    try {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const grid = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
      if (!grid.length) { setImportMsg("File rỗng"); return; }
      const headers = grid[0].map(h => (h || "").toString().trim());
      const idx = (name) => headers.findIndex(h => h.toLowerCase() === name.toLowerCase());
      const iYear = idx("Year"), iMonth = idx("Month"), iName = idx("Promoter Name");
      const iSold = idx("Sellout Result"), iHE = idx("High-end Result");
      const iGross = idx("Gross Income"), iNet = idx("Net salary");
      if (iYear < 0 || iMonth < 0 || iName < 0) {
        setImportMsg("Lỗi: thiếu cột Year / Month / Promoter Name trong file");
        return;
      }
      // Dải cột break-down: từ "Gross Income" (S) tới "Service fee" (AG) — lấy theo vị trí để tránh trùng tên
      // Chỉ lấy các cột bảo hiểm + thuế cần thiết. "Unemployment Insurance (1%)" xuất hiện 2 lần
      // → lần 1 = công ty đóng, lần 2 = người lao động đóng.
      const idxAll = (name) => headers.reduce((acc, h, i) => (h.toLowerCase() === name.toLowerCase() ? [...acc, i] : acc), []);
      const uiCols = idxAll("Unemployment Insurance (1%)");
      const cols = {
        companySI: idx("Social Insurance (17.5%)"),
        companyHI: idx("Health Insurance (3%)"),
        companyUI: uiCols[0] ?? -1,
        tradeUnion: idx("Trade Union (2%)"),
        pai: idx("PAI Insurance"),
        empSI: idx("Social Insurance (8%)"),
        empHI: idx("Health Insurance (1.5%)"),
        empUI: uiCols[1] ?? uiCols[0] ?? -1,
        taxedIncome: idx("Taxed income"),
        pit: idx("PIT amount"),
      };
      const bdFound = Object.entries(cols).filter(([, i]) => i >= 0).map(([k]) => k);
      const promoters = await getAllPromoters();
      const byName = {};
      promoters.forEach(p => { byName[normName(p.name)] = p; });

      const matched = [], unmatched = [];
      for (let r = 1; r < grid.length; r++) {
        const row = grid[r];
        const name = (row[iName] || "").toString().trim();
        if (!name) continue;
        const year = (row[iYear] || "").toString().replace(/\D/g, "");
        const mm = (row[iMonth] || "").toString().replace(/\D/g, "").padStart(2, "0");
        if (!year || !mm || mm === "00") continue;
        const month = `${year}-${mm}`;
        const val = (c) => (c >= 0 ? parseMoney(row[c]) : 0);
        const breakdown = {
          companySI: val(cols.companySI), companyHI: val(cols.companyHI), companyUI: val(cols.companyUI),
          tradeUnion: val(cols.tradeUnion), pai: val(cols.pai),
          empSI: val(cols.empSI), empHI: val(cols.empHI), empUI: val(cols.empUI),
          taxedIncome: val(cols.taxedIncome), pit: val(cols.pit),
        };
        const rec = {
          name, month,
          sold: iSold >= 0 ? Number((row[iSold] || "0").toString().replace(/[^\d]/g, "")) || 0 : 0,
          highendSold: iHE >= 0 ? Number((row[iHE] || "0").toString().replace(/[^\d]/g, "")) || 0 : 0,
          incomeGross: iGross >= 0 ? parseMoney(row[iGross]) : 0,
          incomeNet: iNet >= 0 ? parseMoney(row[iNet]) : 0,
          breakdown,
        };
        const p = byName[normName(name)];
        if (p) matched.push({ ...rec, uid: p.uid });
        else unmatched.push(rec);
      }
      const unmatchedNames = [...new Set(unmatched.map(u => u.name))];
      setImportPreview({ matched, unmatched, unmatchedNames, bdFound });
      setImportMsg(`Đọc xong: ${matched.length} dòng khớp, ${unmatched.length} dòng KHÔNG khớp (${unmatchedNames.length} tên). Nhận diện ${bdFound.length}/10 cột bảo hiểm/thuế.`);
    } catch (err) {
      setImportMsg("Lỗi đọc file: " + err.message);
    }
  };

  const handleConfirmImport = async () => {
    if (!importPreview?.matched?.length) return;
    setImporting(true);
    setImportMsg("Đang import...");
    try {
      let done = 0;
      for (const rec of importPreview.matched) {
        await setIncomeHistory(rec.uid, rec.month, {
          incomeNet: rec.incomeNet, incomeGross: rec.incomeGross,
          sold: rec.sold, highendSold: rec.highendSold,
          breakdown: rec.breakdown || {},
        });
        done++;
      }
      addAuditLog({ type: "income_imported", actorName: "Admin", actorRole: "admin", detail: `Import thu nhập lịch sử từ Excel: ${done} dòng`, meta: {} });
      setImportMsg(`✅ Đã import ${done} dòng thu nhập lịch sử.`);
      setImportPreview(null);
    } catch (err) {
      setImportMsg("Lỗi import: " + err.message);
    } finally { setImporting(false); }
  };

  const calculate = async () => {
    if (!cfgLoaded) return;   // chờ cấu hình tải xong, tránh tính bằng quy tắc mặc định
    setLoading(true);
    const [promoters, sales, targets, programs, eventBonuses, adjs, closingDoc,
      insAdjList, holidays, overtime] = await Promise.all([
      getAllPromoters(),
      getSalesByMonth(month),
      getAllTargets(month),
      getAllBonusPrograms(),
      getEventBonuses(month),
      getPayrollAdjustments(month),
      getPayrollClosing(month),
      getInsuranceAdjustments(month).catch(() => []),
      getHolidays().catch(() => []),
      getOvertimeByMonth(month).catch(() => []),
    ]);
    setAdjustments(adjs);
    setClosing(closingDoc);
    setInsAdjs(insAdjList || []);

    const adjMap = {};
    adjs.forEach(a => { adjMap[a.promoterId] = a; });
    const targetMap = {};
    targets.forEach(t => { targetMap[t.promoterId] = t; });

    const results = await Promise.all(promoters.map(async (p0) => {
      const p = promoterForMonth(p0, month);   // lương cơ bản / phụ cấp của đúng tháng này
      const attendance = await getAttendanceByMonth(p.uid, month);
      const pay = computeMonthlyPay({
        promoter: p, month, cfg,
        attendance,
        sales: sales.filter(x => x.promoterId === p.uid),
        target: targetMap[p.uid] || null,
        programs,
        events: eventBonuses.filter(b => b.promoterId === p.uid),
        adjustment: adjMap[p.uid] || null,
        holidays,
        overtime: overtime.filter(o => o.promoterId === p.uid),
      });
      return { ...p, ...pay, target: targetMap[p.uid] || {}, adjNote: adjMap[p.uid]?.note || "" };
    }));

    setRows(results);
    setLoading(false);
  };

  useEffect(() => { calculate(); }, [month, cfgLoaded, cfg]);


  return (
    <>
      <div className="page-header">
        <div><h1>{t("salary.title")}</h1><p>{t("salary.subtitle")}</p></div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 160 }} />
          <button className="btn" onClick={calculate}>{t("common.recalc")}</button>
          <button className="btn" onClick={() => exportCSV(rows, month)}>{t("salary.export_csv")}</button>
          <button className="btn btn-primary" disabled={exportingXls || !rows.length} onClick={handleExportSalaryXls}>
            {exportingXls ? t("common.exporting") : t("salary.export_xlsx")}
          </button>
        </div>
      </div>

      {loading && <div style={{ color: "#888", padding: 20 }}>{t("common.loading")}</div>}

      {/* ── Review công & chốt sổ ── */}
      <div className="card" style={{ marginBottom: 16, borderLeft: isClosed ? "4px solid #1D9E75" : "4px solid #EF9F27" }}>
        <div className="card-header">
          <div>
            <div className="card-title">{t("pr.title")} — {month.slice(5)}/{month.slice(0, 4)}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
              {t("pr.subtitle")}
            </div>
          </div>
          <button className="btn btn-sm" style={isClosed ? {} : { background: "#1D9E75", color: "#fff", borderColor: "#1D9E75" }}
            onClick={handleToggleClosing}>
            {isClosed ? t("pr.reopen") : t("pr.close")}
          </button>
        </div>
        {isClosed && (
          <div className="alert alert-success" style={{ marginBottom: 10 }}>
            {t("pr.closed_at")} {closing?.closedAt?.seconds ? format(new Date(closing.closedAt.seconds * 1000), "dd/MM/yyyy HH:mm") : ""} {t("pr.locked_note")}
          </div>
        )}
        {!isClosed && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {/* Chọn promoter → nạp sẵn điều chỉnh đã có của tháng, để lưu lần sau không xóa mất
                các khoản đã nhập trước (form ghi đè cả 3 ô mỗi lần lưu) */}
            <select value={adjForm.promoterId} onChange={e => {
              const id = e.target.value;
              const cur = adjustments.find(a => a.promoterId === id);
              const v = (x) => (x ? String(x) : "");
              setAdjForm({
                promoterId: id,
                extraDays: v(cur?.extraDays), extraBonus: v(cur?.extraBonus),
                otNonTax: v(cur?.extraOtNonTax), note: cur?.note || "",
              });
            }} style={{ flex: "1 1 160px", fontSize: 13 }}>
              <option value="">{t("pr.pick_promoter")}</option>
              {rows.map(r => <option key={r.uid} value={r.uid}>{r.name}</option>)}
            </select>
            <input type="number" step="0.5" placeholder={t("pr.days_ph")} value={adjForm.extraDays}
              onChange={e => setAdjForm(f => ({ ...f, extraDays: e.target.value }))} style={{ width: 140, fontSize: 13 }} />
            <input type="number" step="10000" placeholder={t("pr.bonus_ph")} value={adjForm.extraBonus}
              onChange={e => setAdjForm(f => ({ ...f, extraBonus: e.target.value }))} style={{ width: 140, fontSize: 13 }} />
            <input type="number" step="1000" placeholder={t("pr.ot_ph")} value={adjForm.otNonTax}
              onChange={e => setAdjForm(f => ({ ...f, otNonTax: e.target.value }))} style={{ width: 150, fontSize: 13 }} />
            <input placeholder={t("pr.note_ph")} value={adjForm.note}
              onChange={e => setAdjForm(f => ({ ...f, note: e.target.value }))} style={{ flex: "1 1 180px", fontSize: 13 }} />
            <button className="btn btn-primary btn-sm" onClick={handleSaveAdjustment}>{t("pr.save")}</button>
          </div>
        )}
        {adjustments.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {adjustments.map(a => {
              const p = rows.find(r => r.uid === a.promoterId);
              return (
                <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, background: "#f7f7f7", borderRadius: 6, padding: "4px 8px" }}>
                  <span>
                    {p?.name || a.promoterId}:
                    {a.extraDays ? <strong> {a.extraDays > 0 ? "+" : ""}{a.extraDays} công</strong> : null}
                    {a.extraBonus ? <strong> {a.extraBonus > 0 ? "+" : ""}{Number(a.extraBonus).toLocaleString("vi-VN")}đ thưởng CT</strong> : null}
                    {a.extraOtNonTax ? <strong> {a.extraOtNonTax > 0 ? "+" : ""}{Number(a.extraOtNonTax).toLocaleString("vi-VN")}đ OT</strong> : null}
                    {a.note ? ` — ${a.note}` : ""}
                  </span>
                  {!isClosed && <button className="btn btn-sm btn-danger" style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => handleDeleteAdjustment(a)}>✕</button>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card">
        <table className="table-card">
          <thead>
            <tr>
              <th>{t("common.name")}</th><th>{t("salary.days_worked")}</th><th>{t("salary.work_pay")}</th>
              <th>{t("salary.sales_bonus")}</th><th>{t("salary.highend_bonus")}</th><th>{t("salary.program_bonus")}</th>
              <th>{t("salary.event_bonus")}</th>
              <th style={{ textAlign: "right" }}>{t("salary.total")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.uid} onClick={() => setSelected(r)} style={{ cursor: "pointer" }}>
                <td data-label="Họ tên"><strong>{r.name}</strong><div style={{ fontSize: 11, color: "#888" }}>{r.shopName}</div></td>
                <td data-label="Ngày công">
                  {r.dayCreditTotal} công{(r.paidHolidayDays || r.holidayCredits) > 0 ? ` (+${r.paidHolidayDays || r.holidayCredits} lễ)` : ""}
                  {r.adjDays !== 0 && <span style={{ fontSize: 11, color: "#B45309", marginLeft: 4 }} title={r.adjNote}>({r.adjDays > 0 ? "+" : ""}{r.adjDays} ĐC)</span>}
                </td>
                <td data-label="Lương công">{r.workPay.toLocaleString("vi-VN")} đ</td>
                <td data-label="Thưởng DS">{r.salesBonus > 0 ? r.salesBonus.toLocaleString("vi-VN") + " đ" : <span className="badge badge-gray">0</span>}</td>
                <td data-label="Thưởng HE">{r.highendBonus > 0 ? r.highendBonus.toLocaleString("vi-VN") + " đ" : "—"}</td>
                <td data-label="Thưởng CT">
                  {r.bonusProgram > 0 ? r.bonusProgram.toLocaleString("vi-VN") + " đ" : "—"}
                  {r.adjBonus !== 0 && <span style={{ fontSize: 11, color: "#B45309", marginLeft: 4 }} title={r.adjNote}>({r.adjBonus > 0 ? "+" : ""}{r.adjBonus.toLocaleString("vi-VN")} ĐC)</span>}
                </td>
                <td data-label="Thưởng SK">{r.eventBonus > 0 ? r.eventBonus.toLocaleString("vi-VN") + " đ" : "—"}</td>
                <td data-label="Tổng lương"><strong>{r.total.toLocaleString("vi-VN")} đ</strong></td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={7} style={{ fontWeight: 500, paddingTop: 12 }}>Tổng chi lương tháng</td>
                <td style={{ textAlign: "right", fontWeight: 500, fontSize: 15 }}>
                  {rows.reduce((a, r) => a + r.total, 0).toLocaleString("vi-VN")} đ
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ── Tổng chi phí công ty qua agency (chỉ admin) ── */}
      {costCfg && rows.length > 0 && (() => {
        const cc = costCfg;
        const staffRows = rows.filter(r => r.dayCreditTotal > 0);
        const n = staffRows.length;
        const insAdjMap = {};
        insAdjs.forEach(a => { insAdjMap[a.promoterId] = a; });

        const insRows = staffRows.map(r => {
          const adj = insAdjMap[r.uid];
          const probation = isProbationMonth(r.startDate, month, taxCfg.probationMonths);
          // Mặc định: thử việc thì không đóng BH. Admin bật/tắt được và lựa chọn đó thắng.
          const enabled = adj?.enabled != null ? adj.enabled : !probation;
          const excluded = adj?.enabled === false; // admin chủ động loại → bỏ cả PAI
          const overridden = adj?.baseOverride != null;
          const base = enabled ? (overridden ? Number(adj.baseOverride) : Number(r.baseSalary || 0)) : 0;
          const row = {
            uid: r.uid, name: r.name, startDate: r.startDate, dayCreditTotal: r.dayCreditTotal,
            probation, enabled, overridden, base,
            si: base * cc.siPct / 100, hi: base * cc.hiPct / 100,
            ui: base * cc.uiPct / 100, union: base * cc.unionPct / 100,
            pai: excluded ? 0 : (Number(cc.pai) || 0), // BH tai nạn theo đầu người — thử việc vẫn tính
          };
          row.total = row.si + row.hi + row.ui + row.union + row.pai;
          return row;
        });
        const sum = (k) => insRows.reduce((a, x) => a + x[k], 0);
        const insBase = sum("base");
        const si = sum("si"), hi = sum("hi"), ui = sum("ui"), union = sum("union"), pai = sum("pai");
        const insuranceTotal = si + hi + ui + union + pai;
        const feePer = getMgmtFeePerPerson(n, cc.mgmtTiers);
        const mgmtTotal = feePer * n;
        const grossTotal = rows.reduce((a, r) => a + r.total, 0);
        const vat = (grossTotal + insuranceTotal + mgmtTotal) * cc.vatPct / 100;
        const grand = grossTotal + insuranceTotal + mgmtTotal + vat;
        const money = (v) => Math.round(v).toLocaleString("vi-VN") + " đ";
        return (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header">
              <div>
                <div className="card-title">{t("cc.title")} — {month.slice(5)}/{month.slice(0, 4)}</div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                  {n} nhân viên có công · Bảo hiểm tính trên lương cơ bản · Phí quản lý {feePer.toLocaleString("vi-VN")}đ/người
                </div>
              </div>
              <Link className="btn btn-sm" to="/admin/settings?tab=cost">⚙️ {t("cc.config")}</Link>
            </div>

            <div className="salary-breakdown">
              <div className="salary-row" style={{ fontWeight: 600 }}><span>{t("cc.gross")}</span><span>{money(grossTotal)}</span></div>
              <div className="salary-row" style={{ color: "#666", fontSize: 13 }}><span>{t("cc.si")} ({cc.siPct}%)</span><span>{money(si)}</span></div>
              <div className="salary-row" style={{ color: "#666", fontSize: 13 }}><span>{t("cc.hi")} ({cc.hiPct}%)</span><span>{money(hi)}</span></div>
              <div className="salary-row" style={{ color: "#666", fontSize: 13 }}><span>{t("cc.ui")} ({cc.uiPct}%)</span><span>{money(ui)}</span></div>
              <div className="salary-row" style={{ color: "#666", fontSize: 13 }}><span>{t("cc.union")} ({cc.unionPct}%)</span><span>{money(union)}</span></div>
              <div className="salary-row" style={{ color: "#666", fontSize: 13 }}><span>{t("cc.pai")} ({n} × {Number(cc.pai).toLocaleString("vi-VN")}đ)</span><span>{money(pai)}</span></div>
              <div className="salary-row">
                <span>
                  {t("cc.ins_total")}
                  <button className="btn btn-sm" style={{ marginLeft: 8, padding: "1px 8px", fontSize: 11 }}
                    onClick={() => setShowInsDetail(s => !s)}>
                    {showInsDetail ? t("cc.hide_detail") : t("cc.show_detail")}
                  </button>
                </span>
                <span>{money(insuranceTotal)}</span>
              </div>

              {showInsDetail && (
                <div style={{ margin: "6px 0 14px", border: "1px solid #eee", borderRadius: 8, overflowX: "auto" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "#FFF6F6", borderBottom: "1px solid #F3D6D6" }}>
                    <span style={{ fontSize: 12, color: "#666" }}>{t("cc.detail_note")}</span>
                    <button className="btn btn-sm" onClick={() => exportInsuranceCSV(insRows, month, cc)}>{t("cc.export_detail")}</button>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 820 }}>
                    <thead>
                      <tr style={{ background: "#fafafa", textAlign: "right" }}>
                        <th style={{ padding: "6px 8px", textAlign: "left" }}>{t("common.name")}</th>
                        <th style={{ padding: "6px 8px", textAlign: "left" }}>{t("prom.start_date")}</th>
                        <th style={{ padding: "6px 8px" }}>{t("cc.ins_base")}</th>
                        <th style={{ padding: "6px 8px" }}>{t("cc.si")}</th>
                        <th style={{ padding: "6px 8px" }}>{t("cc.hi")}</th>
                        <th style={{ padding: "6px 8px" }}>{t("cc.ui")}</th>
                        <th style={{ padding: "6px 8px" }}>{t("cc.union")}</th>
                        <th style={{ padding: "6px 8px" }}>PAI</th>
                        <th style={{ padding: "6px 8px" }}>{t("common.total")}</th>
                        <th style={{ padding: "6px 8px", textAlign: "center" }}>{t("cc.insured")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {insRows.map(x => (
                        <tr key={x.uid} style={{ borderTop: "1px solid #f0f0f0", textAlign: "right", opacity: x.enabled ? 1 : 0.55 }}>
                          <td style={{ padding: "6px 8px", textAlign: "left", fontWeight: 500 }}>
                            {x.name}
                            {x.dayCreditTotal < 14 && (
                              <span title={t("cc.under14_note")} style={{ color: "#b45309", marginLeft: 4 }}>⚠</span>
                            )}
                          </td>
                          <td style={{ padding: "6px 8px", textAlign: "left", color: "#888" }}>
                            {x.startDate ? x.startDate.split("-").reverse().join("/") : <span style={{ color: "#C8152A" }}>⚠ —</span>}
                            {x.probation && (
                              <div style={{ fontSize: 10, color: "#b45309" }}>
                                {t("cc.probation_until")} {probationEndDate(x.startDate, taxCfg.probationMonths)?.toLocaleDateString("vi-VN")}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: "6px 8px" }}>
                            {/* Ô không kiểm soát (defaultValue + key) để gõ được;
                                key đổi khi giá trị đã lưu đổi → ô tự đồng bộ lại */}
                            <input type="number" key={`${x.uid}-${x.overridden ? x.base : "auto"}`}
                              defaultValue={x.overridden ? x.base : ""}
                              placeholder={Number(x.base || 0).toLocaleString("vi-VN")}
                              disabled={!x.enabled}
                              onBlur={e => {
                                const v = e.target.value === "" ? null : Number(e.target.value);
                                if (v !== (x.overridden ? x.base : null)) handleInsAdj(x, { baseOverride: v });
                              }}
                              style={{ width: 110, fontSize: 12, textAlign: "right",
                                border: x.overridden ? "1.5px solid #C8152A" : "1px solid #e5e7eb", borderRadius: 4, padding: "2px 6px" }} />
                          </td>
                          <td style={{ padding: "6px 8px" }}>{money(x.si)}</td>
                          <td style={{ padding: "6px 8px" }}>{money(x.hi)}</td>
                          <td style={{ padding: "6px 8px" }}>{money(x.ui)}</td>
                          <td style={{ padding: "6px 8px" }}>{money(x.union)}</td>
                          <td style={{ padding: "6px 8px" }}>{money(x.pai)}</td>
                          <td style={{ padding: "6px 8px", fontWeight: 600 }}>{money(x.total)}</td>
                          <td style={{ padding: "6px 8px", textAlign: "center" }}>
                            <input type="checkbox" checked={x.enabled} style={{ width: "auto", cursor: "pointer" }}
                              onChange={e => handleInsAdj(x, { enabled: e.target.checked })} />
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: "2px solid #ddd", background: "#fafafa", textAlign: "right", fontWeight: 700 }}>
                        <td style={{ padding: "8px" }} colSpan={2}>{t("common.total")}</td>
                        <td style={{ padding: "8px" }}>{money(insBase)}</td>
                        <td style={{ padding: "8px" }}>{money(si)}</td>
                        <td style={{ padding: "8px" }}>{money(hi)}</td>
                        <td style={{ padding: "8px" }}>{money(ui)}</td>
                        <td style={{ padding: "8px" }}>{money(union)}</td>
                        <td style={{ padding: "8px" }}>{money(pai)}</td>
                        <td style={{ padding: "8px" }}>{money(insuranceTotal)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              <div className="salary-row"><span>{t("cc.mgmt")} ({n} × {feePer.toLocaleString("vi-VN")}đ)</span><span>{money(mgmtTotal)}</span></div>
              <div className="salary-row" style={{ color: "#A32D2D" }}><span>VAT ({cc.vatPct}% × Gross + phí + quản lý)</span><span>{money(vat)}</span></div>
              <div className="salary-row" style={{ fontWeight: 700, fontSize: 15, borderTop: "1px solid #eee", paddingTop: 10, marginTop: 6 }}>
                <span>{t("cc.total")}</span><span>{money(grand)}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{t("salary.detail_title")} — {selected.name}</div>
              <button className="btn btn-sm" onClick={() => setSelected(null)}>✕</button>
            </div>
            <SalaryBreakdown month={month} taxCfg={taxCfg} workCfg={cfg.work} row={{
              ...selected,
              sellTarget: selected.target?.sellTarget || 0,
              highendTarget: selected.target?.highendTarget || 0,
            }} />
          </div>
        </div>
      )}

      {/* Import thu nhập lịch sử từ Excel */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title" style={{ marginBottom: 6 }}>📥 Import thu nhập cũ (Excel)</div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>
          Dùng cho dữ liệu trước khi app chạy chính thức (7/2026). File .xlsx cần có cột: Year, Month (M01–M12), Promoter Name, Sellout Result, High-end Result, Gross Income, Net salary. Ghép promoter theo <strong>tên</strong>.
        </div>
        <input type="file" accept=".xlsx" onChange={handleImportFile} style={{ fontSize: 13 }} />
        {importMsg && (
          <div className={`alert ${importMsg.startsWith("✅") ? "alert-success" : importMsg.startsWith("Lỗi") ? "alert-error" : "alert-info"}`} style={{ marginTop: 10 }}>
            {importMsg}
          </div>
        )}
        {importPreview && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: importPreview.bdFound?.length >= 8 ? "#3B6D11" : "#A32D2D", marginBottom: 8 }}>
              {importPreview.bdFound?.length
                ? `✓ Nhận diện ${importPreview.bdFound.length}/10 cột bảo hiểm/thuế`
                : "⚠ Không nhận diện được cột bảo hiểm/thuế. Kiểm tra header file."}
            </div>
            {importPreview.unmatchedNames.length > 0 && (
              <div style={{ fontSize: 12, color: "#A32D2D", marginBottom: 8 }}>
                ⚠ Tên chưa khớp (bỏ qua): {importPreview.unmatchedNames.join(", ")}
              </div>
            )}
            <button className="btn btn-primary btn-sm" onClick={handleConfirmImport} disabled={importing || importPreview.matched.length === 0}>
              {importing ? "Đang import..." : `Import ${importPreview.matched.length} dòng khớp`}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// Xuất bảng bảo hiểm theo từng người để gửi agency đối chiếu
function exportInsuranceCSV(insRows, month, cc) {
  const header = ["Promoter", "Ngày vào làm", "Thử việc", "Đóng BH", "Lương đóng BH",
    `BHXH ${cc.siPct}%`, `BHYT ${cc.hiPct}%`, `BHTN ${cc.uiPct}%`, `Công đoàn ${cc.unionPct}%`, "PAI", "Tổng"];
  const body = insRows.map(x => [
    x.name, x.startDate || "", x.probation ? "Có" : "", x.enabled ? "Có" : "Không",
    Math.round(x.base), Math.round(x.si), Math.round(x.hi), Math.round(x.ui),
    Math.round(x.union), Math.round(x.pai), Math.round(x.total),
  ]);
  const sum = (k) => Math.round(insRows.reduce((a, x) => a + x[k], 0));
  body.push(["TỔNG", "", "", "", sum("base"), sum("si"), sum("hi"), sum("ui"), sum("union"), sum("pai"), sum("total")]);
  const csv = [header, ...body].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `bao-hiem_${month}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function exportCSV(rows, month) {
  const header = ["Họ tên", "Shop", "Ngày công", "Lương công", "Phụ cấp", "Thưởng DS", "Thưởng HE", "Thưởng CT", "Thưởng SK", "Tổng"];
  const lines = [header.join(","), ...rows.map(r =>
    [r.name, r.shopName, r.dayCreditTotal, r.workPay, r.allowance || 0, r.salesBonus, r.highendBonus, r.bonusProgram, r.eventBonus, r.total].join(",")
  )];
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `luong_${month}.csv`; a.click();
}
